import axios from 'axios';
// Make sure axios is available globally for direct calls
window.axios = axios;

// Determine the default API URL dynamically
const getDefaultApiUrl = () => {
  // If we're in a browser environment, use the current origin
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:5002/api'; // Fallback for non-browser environments
};

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5002/api',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 15000 // 15 seconds timeout
});

// Add request interceptor
api.interceptors.request.use(
  config => {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    // If token exists, add it to headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  error => Promise.reject(error)
);

// Add response interceptor
api.interceptors.response.use(
  response => response,
  error => {
    // Handle token expiration or authentication errors
    if (error.response && error.response.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/auth/login';
    }
    
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: async (credentials) => {
    // Logging the API base URL
    console.log('API Base URL:', api.defaults.baseURL);
    
    // Logging the full login endpoint
    const loginEndpoint = `${api.defaults.baseURL}/auth/login`;
    console.log('Full Login Endpoint:', loginEndpoint);
    
    // Logging request headers and data
    console.log('Request Headers:', {
      'Content-Type': 'application/json',
      'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : 'None'
    });
    console.log('Request Data:', JSON.stringify(credentials));
    
    // Use direct URL for debugging
    const directUrl = 'http://localhost:5002/api/auth/login';
    
    try {
      // Making the actual API call
      console.log('Initiating login API call with direct URL...');
      console.log('Using direct axios call to URL:', directUrl);
      
      // Try using direct axios for debugging
      const response = await axios.post(directUrl, credentials, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 15000
      });
      
      // Logging successful response
      console.log('Login API Response Status:', response.status);
      console.log('Login API Response Headers:', response.headers);
      console.log('Login API Response Data:', response.data);
      
      return response;
    } catch (error) {
      console.error('Direct axios login failed, trying fallback approach...', error);
      
      try {
        // Fallback to using fetch API directly
        console.log('Trying fetch API as fallback...');
        const fetchResponse = await fetch(directUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(credentials)
        });
        
        const data = await fetchResponse.json();
        console.log('Fetch fallback response:', data);
        
        // Convert to axios-like response format
        return {
          status: fetchResponse.status,
          headers: Object.fromEntries(fetchResponse.headers.entries()),
          data: data
        };
      } catch (fetchError) {
        console.error('Fetch fallback also failed:', fetchError);
      }
      // Logging error details
      console.error('Login API Error:', error.message);
      if (error.response) {
        console.error('Login API Error Status:', error.response.status);
        console.error('Login API Error Data:', error.response.data);
        console.error('Login API Error Headers:', error.response.headers);
      } else if (error.request) {
        console.error('Login API No Response Received:', error.request);
      }
      
      throw error; // Re-throw for handling in the caller
    }
  },
  register: userData => api.post('/auth/register', userData),
  getMe: async () => {
    // Always using real API for getMe
    console.log('Using real getMe API');
    
    // Default to real API call
    return api.get('/auth/me');
  }
};

// Vehicle services
export const vehicleService = {
  getVehicles: params => api.get('/vehicles', { params }),
  getVehicle: id => api.get(`/vehicles/${id}`),
  createVehicle: vehicleData => api.post('/vehicles', vehicleData),
  updateVehicle: (id, vehicleData) => api.put(`/vehicles/${id}`, vehicleData),
  deleteVehicle: id => api.delete(`/vehicles/${id}`),
  updatePosition: (id, positionData) => api.post(`/vehicles/${id}/position`, positionData)
};

// Geofence services
export const geofenceService = {
  getGeofences: params => api.get('/geofences', { params }),
  getGeofence: id => api.get(`/geofences/${id}`),
  createGeofence: geofenceData => api.post('/geofences', geofenceData),
  updateGeofence: (id, geofenceData) => api.put(`/geofences/${id}`, geofenceData),
  deleteGeofence: id => api.delete(`/geofences/${id}`),
  getVehiclesInGeofence: id => api.get(`/geofences/${id}/vehicles`)
};

// Alert services
export const alertService = {
  getAlerts: params => api.get('/alerts', { params }),
  getAlert: id => api.get(`/alerts/${id}`),
  createAlert: alertData => api.post('/alerts', alertData),
  acknowledgeAlert: id => api.put(`/alerts/${id}/acknowledge`),
  deleteAlert: id => api.delete(`/alerts/${id}`)
};

// User services
export const userService = {
  getUsers: params => api.get('/users', { params }),
  getUser: id => api.get(`/users/${id}`),
  createUser: userData => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: id => api.delete(`/users/${id}`)
};

export default api;